import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;
const inputCls =
  'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';

// ── Modal ─────────────────────────────────────────────────────────────────────
function LoanTypeModal({ open, onClose, onSaved, initial, authConfig }) {
  const [name, setName] = useState('');
  const [docs, setDocs] = useState(['']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const nameRef = useRef(null);

  // populate when editing
  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setDocs(initial?.requiredDocuments?.length ? [...initial.requiredDocuments] : ['']);
    setError('');
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [open, initial]);

  const addDoc = () => setDocs((d) => [...d, '']);
  const removeDoc = (i) => setDocs((d) => d.filter((_, idx) => idx !== i));
  const changeDoc = (i, val) =>
    setDocs((d) => d.map((v, idx) => (idx === i ? val : v)));

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) { setError('Loan type name is required.'); return; }
    const requiredDocuments = docs.map((d) => d.trim()).filter(Boolean);
    setSaving(true);
    setError('');
    try {
      const payload = { name: trimmedName, requiredDocuments };
      if (initial?._id) {
        await axios.put(`${API}/loan-types/${initial._id}`, payload, authConfig);
      } else {
        await axios.post(`${API}/loan-types`, payload, authConfig);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/10 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">
            {initial?._id ? 'Edit Loan Type' : 'Add Loan Type'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Loan Type Name <span className="text-red-500">*</span>
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="e.g. Home Loan, Personal Loan…"
              className={`${inputCls} w-full`}
            />
          </div>

          {/* Required Documents */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Required Documents
            </label>
            <div className="space-y-2">
              {docs.map((doc, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={doc}
                    onChange={(e) => changeDoc(i, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addDoc(); }
                    }}
                    placeholder={`Document ${i + 1}`}
                    className={`${inputCls} flex-1`}
                  />
                  {docs.length > 1 && (
                    <button
                      onClick={() => removeDoc(i)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addDoc}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Plus size={13} /> Add another document
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !name.trim()}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : initial?._id ? 'Save Changes' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function LoanTypeList() {
  const { userInfo } = useSelector((s) => s.userLogin || {});
  const authConfig = {
    headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {},
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null); // null = add, object = edit

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/loan-types`, authConfig);
      setItems(data);
    } catch (err) {
      setMessage(err?.response?.data?.message || 'Failed to load loan types.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => { setEditItem(null); setModalOpen(true); };
  const openEdit = (item) => { setEditItem(item); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);
  const handleSaved = () => { setMessage(''); fetchList(); };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this loan type?')) return;
    setMessage('');
    try {
      await axios.delete(`${API}/loan-types/${id}`, authConfig);
      fetchList();
    } catch (err) {
      setMessage(err?.response?.data?.message || 'Failed to delete.');
    }
  };

  return (
    <div className="px-2 py-3 sm:p-6 bg-gray-50 min-h-screen">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Loan Types</h1>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <Plus size={16} />
          Add Loan Type
        </button>
      </div>

      {message && (
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 mb-4">
          {message}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-12">
            No loan types added yet.
          </p>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 w-14">
                  S.No
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  Loan Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  Required Documents
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, index) => (
                <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.name}</td>
                  <td className="px-4 py-3">
                    {item.requiredDocuments?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {item.requiredDocuments.map((doc, i) => (
                          <span
                            key={i}
                            className="inline-block bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full border border-blue-100"
                          >
                            {doc}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(item)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        title="Edit"
                      >
                        <Pencil size={15} strokeWidth={2.2} />
                      </button>
                      <button
                        onClick={() => deleteItem(item._id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} strokeWidth={2.2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      <LoanTypeModal
        open={modalOpen}
        onClose={closeModal}
        onSaved={handleSaved}
        initial={editItem}
        authConfig={authConfig}
      />
    </div>
  );
}
